import React, { useCallback, useEffect, useRef, useState } from "react"
import { useAppSelector } from "@/store/hooks"
import { getUserCreds, isMobile, socket } from "@/utils/utils"
import { gameRoles } from "@/utils/gameRoles"
import ModalButton from "@/Components/ModalButton"
import classNames from "classnames"
import GameTimer from "@/Components/GameTimer"
import { featureFlags } from "@mono/constants"
import { images } from "@/utils/data"
import { useGameTimings } from "@/Hooks/useGameTimings"
import { Swiper, SwiperSlide } from "swiper/react"

import "swiper/css"
import "swiper/css/free-mode"
import "swiper/css/navigation"

export default function RolePickingModal() {
  const myGame = useAppSelector((state) => state.global.myGame)
  const { rolePickingTotalTime } = useGameTimings()
  const [selectedRole, setSelectedRole] = useState<GameRoleId | null>(null)
  const [previewRole, setPreviewRole] = useState<GameRoleId | null>(null)
  const [hasPickedRole, setHasPickedRole] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [canScrollMore, setCanScrollMore] = useState(false)
  const disabledRoles = new Set<GameRoleId>(featureFlags.disabledRoles || [])
  const availableRoles = gameRoles.filter((role) => role.id !== "none")
  const mobile = isMobile()
  const lockedRoleTitles = availableRoles
    .filter((role) => disabledRoles.has(role.id))
    .map((role) => role.title)

  useEffect(() => {
    if (!previewRole && availableRoles.length > 0) {
      setPreviewRole(availableRoles[0].id)
    }
  }, [availableRoles, previewRole])

  // A few pixels of slack, otherwise fractional heights leave the hint stuck on
  // even when the list is scrolled all the way down.
  const updateScrollHint = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    setCanScrollMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4)
  }, [])

  // The cards are images, so the scroll height only settles once they load; a
  // ResizeObserver keeps the hint honest as the content and the modal resize.
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    updateScrollHint()
    const observer = new ResizeObserver(updateScrollHint)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [updateScrollHint, myGame?.situation?.pickingRoles, hasPickedRole])

  // `100vh` on a phone is the height *without* the browser chrome, so a
  // vh-sized modal hangs below the visible area and the full-height cards get
  // clipped. `window.innerHeight` is the height actually on screen.
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight)
  useEffect(() => {
    const syncViewportHeight = () => setViewportHeight(window.innerHeight)
    syncViewportHeight()
    window.addEventListener("resize", syncViewportHeight)
    window.addEventListener("orientationchange", syncViewportHeight)
    window.visualViewport?.addEventListener("resize", syncViewportHeight)
    return () => {
      window.removeEventListener("resize", syncViewportHeight)
      window.removeEventListener("orientationchange", syncViewportHeight)
      window.visualViewport?.removeEventListener("resize", syncViewportHeight)
    }
  }, [])

  const handleRolePick = (role: GameRoleId) => {
    if (!myGame || hasPickedRole) return
    if (disabledRoles.has(role)) return
    const player = myGame.players.find((p) => p.email === getUserCreds().email)
    if (!player) return

    setSelectedRole(role)
  }

  const handleConfirmRole = () => {
    if (!myGame || !selectedRole || hasPickedRole) return
    const player = myGame.players.find((p) => p.email === getUserCreds().email)
    if (!player) return

    socket.emit("game.rolePicked", {
      gameId: myGame.id,
      roleId: selectedRole,
      playerId: player.email,
    })
    setHasPickedRole(true)
  }

  // Create a mapping between role IDs and their corresponding images
  const roleImageMapping: Record<string, string> = {
    "rent-lord": images.roleCards[0],
    "wall-street-wolf": images.roleCards[1],
    "private-buyer": images.roleCards[2],
    "auction-strategist": images.roleCards[3],
    "card-stealer": images.roleCards[4],
    financer: images.roleCards[5],
  }

  const renderRoleCard = (role: IGameRole) => {
    if (!myGame) return null
    const isRoleTaken = myGame.players.some((p) => p.role === role.id)
    const isRoleLocked = disabledRoles.has(role.id)

    const roleImage = roleImageMapping[role.id]
    const canSelectRole = !isRoleTaken && !hasPickedRole && !isRoleLocked
    const isSelected = selectedRole === role.id

    return (
      <div
        key={role.id}
        className={classNames(
          "rounded-2xl transition-all duration-300 bg-opacity-90 relative select-none shadow-xl animate-fadeInCard text-white overflow-hidden",
          {
            // Mobile shows one full-height row: the height comes from the modal
            // and the width follows the 2:3 artwork, so nothing overflows.
            "h-full w-auto aspect-[2/3]": mobile,
            "cursor-pointer hover:-translate-y-1": !hasPickedRole,
            "cursor-not-allowed": !canSelectRole,
            "opacity-80": isRoleTaken,
            "opacity-95 ring-2 ring-amber-300/70 shadow-[0_0_28px_rgba(255,191,71,0.4)]":
              isRoleLocked,
          }
        )}
        style={{
          // The mobile carousel clips anything that spills out of a slide, so
          // the wide desktop glow would only ever show up as a cut-off band.
          boxShadow: isSelected
            ? mobile
              ? "0 0 16px 5px rgba(0, 174, 20, 0.55)"
              : "0 8px 32px 0 rgba(0, 123, 255, 0.25), 0 0px 20px 15px rgba(0, 174, 20, 0.7)"
            : "0 4px 16px 0 rgba(0,0,0,0.18)",
          borderImage: isSelected
            ? "linear-gradient(90deg, #60a5fa, #34d399) 1"
            : undefined,
        }}
        onClick={() => {
          setPreviewRole(role.id)
          if (canSelectRole) {
            handleRolePick(role.id)
          }
        }}
      >
        {roleImage ? (
          <img
            src={roleImage}
            alt={role.title}
            className={classNames(
              "object-cover",
              mobile ? "h-full w-full" : "w-full",
              {
                "brightness-[0.38]": isRoleLocked,
                "brightness-[0.55]": isRoleTaken && !isRoleLocked,
                // Pushing the unpicked cards back makes the picked one read as
                // picked even where its glow is clipped by the carousel.
                "brightness-[0.6]":
                  mobile &&
                  !!selectedRole &&
                  !isSelected &&
                  !isRoleTaken &&
                  !isRoleLocked,
              }
            )}
          />
        ) : null}

        {isRoleLocked ? (
          <>
            <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(135deg,rgba(255,184,77,0.28),rgba(255,120,60,0.12),rgba(25,20,12,0.65))]"></div>
            <div className="absolute inset-0 pointer-events-none bg-[repeating-linear-gradient(130deg,rgba(255,207,120,0.22)_0px,rgba(255,207,120,0.22)_8px,rgba(0,0,0,0)_8px,rgba(0,0,0,0)_16px)]"></div>
            <div
              className={classNames(
                "absolute top-3 right-3 rounded-xl border border-amber-100/80 bg-gradient-to-br from-amber-200/30 to-orange-300/20 flex items-center justify-center text-amber-50 backdrop-blur-md shadow-[0_0_14px_rgba(255,194,77,0.7)] animate-pulse",
                "h-10 w-10"
              )}
            >
              <i
                className={classNames(
                  "fa-solid fa-lock drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]",
                  "text-sm"
                )}
              ></i>
            </div>
            <div
              className={classNames(
                "absolute left-2 right-2 bottom-2 rounded-md border border-amber-200/40 bg-black/55 px-2 text-center font-semibold text-amber-100 backdrop-blur-sm",
                mobile ? "py-1.5 text-sm" : "py-1 text-[11px]"
              )}
            >
              Not available in demo
            </div>
          </>
        ) : null}

        {/* An outer glow is at the mercy of the carousel's clipping, so on
            mobile the selection is drawn *inside* the card too, where nothing
            can cut it off. */}
        {mobile && isSelected ? (
          <>
            <div className="absolute inset-0 rounded-2xl border-[3px] border-emerald-400 shadow-[inset_0_0_26px_rgba(16,185,129,0.6)] pointer-events-none"></div>
            <div className="absolute top-2 left-2 h-9 w-9 rounded-full bg-emerald-500 border-2 border-white/80 flex items-center justify-center text-white shadow-lg pointer-events-none">
              <i className="fa-solid fa-check"></i>
            </div>
          </>
        ) : null}
      </div>
    )
  }

  if (!myGame?.situation?.pickingRoles) return null

  const myRole = myGame.players.find(
    (p) => p.email === getUserCreds().email
  )?.role
  const previewRoleData = gameRoles.find((role) => role.id === previewRole)
  const showPickedRole = hasPickedRole || !!myRole
  // The mobile carousel is one full-height row, so it never scrolls vertically.
  const showRoleCarousel = mobile && !showPickedRole
  const showMoreRolesHint =
    !mobile && !showPickedRole && availableRoles.length > 3 && canScrollMore

  const hiddenRoles: GameRoleId[] =
    myGame.challengeId === "win-as-rent-lord-without-houses"
      ? gameRoles
          .filter((role) => role.id !== "rent-lord")
          .map((role) => role.id)
      : []
  const finalAvailableRoles = availableRoles.filter(
    (role) => !hiddenRoles.includes(role.id)
  )
  return (
    <div
      className={classNames(
        "fixed inset-0 z-50 flex justify-center bg-black bg-opacity-70 animate-fadeIn",
        mobile ? "items-start" : "items-center"
      )}
    >
      <div
        className={classNames(
          "max-w-[1200px] flex flex-col border border-gray-700 shadow-2xl bg-black/80 backdrop-blur-xl overflow-hidden animate-fadeInModal",
          mobile ? "w-full" : "w-[95vw] max-h-[92vh] rounded-2xl"
        )}
        style={mobile ? { height: viewportHeight } : undefined}
      >
        <div
          className={classNames(
            "border-b border-gray-700 flex flex-col gap-2 shrink-0",
            // Rounded phone corners / notches eat the ends of a full-bleed row,
            // so the header keeps clear of the safe-area insets on mobile.
            mobile
              ? "pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[max(1.75rem,env(safe-area-inset-top))] pb-7"
              : "p-7"
          )}
        >
          <div className="flex justify-between items-center gap-3">
            {/* The title side is the only part allowed to give up width: the
                button must never be pushed under the modal's overflow-hidden
                right edge. */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <h2
                className={classNames(
                  "font-extrabold text-white font-poppins tracking-tight drop-shadow-sm min-w-0 truncate",
                  "text-3xl"
                )}
              >
                Pick Your Role
              </h2>
              <div className="shrink-0">
                <GameTimer
                  timerKey="rolePickingTimeLeft"
                  totalTime={rolePickingTotalTime}
                  fallback={0}
                  size="md"
                  labelText="Time Left"
                  className="text-white"
                />
              </div>
            </div>

            <ModalButton
              color="green"
              size="lg"
              onClick={handleConfirmRole}
              // The button sits in a full-bleed row, so it must not shrink (its
              // own overflow-hidden would clip the label) and must not scale up
              // past the edge of the screen on press.
              className={classNames(
                "shrink-0 whitespace-nowrap hover:scale-100 mr-16",
                {
                  hidden: !mobile,
                }
              )}
              disabled={!selectedRole}
            >
              <div className="flex items-center justify-center gap-2.5">
                <i className="fa-check fa-solid fa-lg text-white"></i>
                <div className="text-white font-medium font-poppins text-lg">
                  Confirm Selection
                </div>
              </div>
            </ModalButton>
          </div>
          <p
            className={classNames(
              "text-gray-400 mt-1 font-poppins text-lg",
              mobile ? "hidden" : "sm:hidden"
            )}
          >
            Choose a role that matches your playstyle. Each role has unique
            abilities and restrictions.
          </p>
        </div>

        <div
          ref={scrollContainerRef}
          onScroll={updateScrollHint}
          className={classNames("flex-1 min-h-0 bg-transparent relative", {
            // The cards are full-height, so every pixel of padding here comes
            // straight out of the card size: just enough to keep the selection
            // glow off the edges of the row.
            "overflow-hidden px-3 py-2": showRoleCarousel,
            "overflow-y-auto p-4": mobile && !showRoleCarousel,
            "overflow-y-auto p-8": !mobile,
          })}
        >
          {showPickedRole ? (
            <div className="flex flex-col items-center gap-8 animate-fadeInCard">
              <h3 className="text-2xl font-poppins font-semibold text-white mb-2">
                You have selected:
              </h3>
              {(() => {
                const selectedRoleData = gameRoles.find(
                  (r) => r.id === (myRole || selectedRole)
                )
                if (!selectedRoleData) return null
                const selectedRoleImage = roleImageMapping[selectedRoleData.id]
                return (
                  <div className="w-full max-w-md p-6 rounded-2xl border-2 border-blue-500 bg-gradient-to-br from-blue-900/90 to-blue-800/80 shadow-2xl animate-fadeInCard">
                    <div className="flex items-center gap-4 mb-4">
                      {selectedRoleImage && (
                        <div className="flex-shrink-0">
                          <img
                            src={selectedRoleImage}
                            alt={`${selectedRoleData.title} icon`}
                            className="w-20 h-20 md:w-16 md:h-16 rounded-xl object-contain shadow-lg"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="text-2xl font-poppins text-white font-bold mb-1">
                          {selectedRoleData.title}
                        </div>
                        <div className="text-base font-medium text-gray-300 font-poppins">
                          {selectedRoleData.subTitle}
                        </div>
                        <span className="text-sm text-green-400 font-poppins font-semibold">
                          (Your Role)
                        </span>
                      </div>
                    </div>
                    <div className="text-base md:text-sm text-green-300 font-poppins bg-gray-800/40 p-2 rounded-md border border-green-400/20">
                      {selectedRoleData.effect}
                    </div>
                  </div>
                )
              })()}
              <p className="md:text-base text-xl text-gray-400 font-poppins mt-2">
                Waiting for other players to pick their roles...
              </p>
            </div>
          ) : mobile ? (
            <Swiper
              spaceBetween={18}
              slidesPerView="auto"
              freeMode={true}
              className="h-full w-full"
            >
              {/* The slide padding is the only vertical room the glow gets:
                  the swiper clips to its own box, and the card fills the
                  slide's content area. */}
              {finalAvailableRoles.map((role) => (
                <SwiperSlide key={role.id} className="!h-full !w-auto py-2.5">
                  {renderRoleCard(role)}
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="grid grid-cols-3 gap-6 w-full animate-fadeInCard mb-6">
              {finalAvailableRoles.map((role) => (
                <div key={role.id}>{renderRoleCard(role)}</div>
              ))}
            </div>
          )}
          {showMoreRolesHint ? (
            <div className="sticky bottom-2 z-10 flex justify-center pointer-events-none">
              <div className="rounded-full border border-white/30 bg-black/55 px-4 py-2 text-white/90 text-xs font-semibold font-poppins shadow-lg backdrop-blur-md">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-arrow-down animate-bounce"></i>
                  <span>Scroll down for more roles</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {!hasPickedRole && (
          <div className="p-7 sm:hidden border-t border-gray-700 text-center bg-transparent">
            <ModalButton
              color="green"
              onClick={handleConfirmRole}
              className="w-full max-w-[20rem]"
              disabled={!selectedRole}
            >
              <div className="flex items-center justify-center gap-2">
                <i className="fa-check fa-solid text-white fa-xl"></i>
                <div className="text-white font-medium font-poppins text-lg">
                  Confirm Selection
                </div>
              </div>
            </ModalButton>
          </div>
        )}
      </div>
    </div>
  )
}
